import { App as CapacitorApp } from '@capacitor/app';
import {
  authorizeAndRegisterCallsLiveDeviceEvidence,
  type AuthorizedCallsLiveDeviceEvidence,
  type CallsLiveDeviceEvidenceCandidate,
} from './CallsLiveDeviceEvidenceAuthority';
import {
  recordCallsLiveDeviceEvidence,
  type CallsLiveDeviceScenario,
} from './CallsLiveDeviceEvidenceRegistry';
import { emitCallsRuntimeEvent } from '@/lib/governorEventBus';

const EVIDENCE_QUERY_PARAMETER =
  'nativeCallEvidence';

const seenEvidenceIds = new Set<string>();
const lastSequenceByDevice =
  new Map<string, number>();

let publicationBridgeStarted = false;

type NativeEvidencePayload = Readonly<{
  evidenceId?: unknown;
  callId?: unknown;
  sessionId?: unknown;
  deviceId?: unknown;
  userId?: unknown;
  eventType?: unknown;
  capturedAt?: unknown;
  sequence?: unknown;
  success?: unknown;
  source?: unknown;
  detail?: unknown;
}>;

function nonEmptyString(
  value: unknown,
): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();

  return normalized.length > 0
    ? normalized
    : null;
}

function resolveScenario(
  eventType: string,
): CallsLiveDeviceScenario | null {
  switch (eventType) {
    case 'CALL_PUSH_RECEIVED':
      return 'background';

    case 'CALL_NOTIFICATION_DISPLAYED':
      return 'locked-screen';

    case 'CALL_SCREEN_OPENED':
      return 'foreground';

    case 'CALL_ANSWERED':
      return 'answer';

    case 'CALL_DECLINED':
      return 'decline';

    case 'CALL_NOTIFICATION_TIMEOUT':
      return 'missed-call';

    default:
      return null;
  }
}

function registryWriter(
  evidence: AuthorizedCallsLiveDeviceEvidence,
  payload: NativeEvidencePayload,
) {
  const scenario = resolveScenario(
    evidence.event,
  );

  if (!scenario) {
    return null;
  }

  return recordCallsLiveDeviceEvidence({
    scenario,
    outcome:
      payload.success === false
        ? 'fail'
        : 'pass',
    source: 'android-runtime',
    deviceId: evidence.deviceId,
    callId: evidence.sessionId,
    operatorId:
      nonEmptyString(payload.userId) ??
      undefined,
    capturedAt: evidence.capturedAt,
    detail: JSON.stringify({
      eventType: evidence.event,
      sessionId: evidence.sessionId,
      evidenceId: evidence.evidenceId,
      sequence:
        evidence.sequence ?? null,
      source:
        nonEmptyString(payload.source) ??
        'android-runtime',
      detail:
        nonEmptyString(payload.detail) ??
        null,
    }),
  });
}

function parsePayloads(
  rawValue: string,
): NativeEvidencePayload[] {
  try {
    const parsed: unknown =
      JSON.parse(rawValue);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (
        entry,
      ): entry is NativeEvidencePayload =>
        typeof entry === 'object' &&
        entry !== null,
    );
  } catch {
    return [];
  }
}

function publishPayload(
  payload: NativeEvidencePayload,
  activeSessionId: string,
): void {
  const evidenceId =
    nonEmptyString(payload.evidenceId);
  const sessionId =
    nonEmptyString(payload.sessionId);
  const deviceId =
    nonEmptyString(payload.deviceId);
  const eventType =
    nonEmptyString(payload.eventType);
  const capturedAt =
    nonEmptyString(payload.capturedAt);

  if (
    !evidenceId ||
    !sessionId ||
    !deviceId ||
    !eventType ||
    !capturedAt
  ) {
    emitCallsRuntimeEvent({
      source:
        'CallsNativeEvidencePublicationBridge',
      stage:
        'NATIVE_CALL_EVIDENCE_REJECTED',
      status: 'failure',
      callSessionId: sessionId,
      callId:
        nonEmptyString(payload.callId) ??
        sessionId,
      reason:
        'Native evidence payload is missing required identity.',
      timestamp:
        new Date().toISOString(),
    });
    return;
  }

  const sequence =
    typeof payload.sequence === 'number' &&
    Number.isSafeInteger(payload.sequence)
      ? payload.sequence
      : undefined;

  const candidate:
    CallsLiveDeviceEvidenceCandidate = {
      evidenceId,
      sessionId,
      deviceId,
      event: eventType,
      capturedAt,
      receivedAt:
        new Date().toISOString(),
      sequence,
      metadata: Object.freeze({
        callId:
          nonEmptyString(payload.callId) ??
          sessionId,
        userId:
          nonEmptyString(payload.userId),
        success:
          payload.success !== false,
        source:
          nonEmptyString(payload.source) ??
          'android-runtime',
        detail:
          nonEmptyString(payload.detail),
      }),
    };

  const result =
    authorizeAndRegisterCallsLiveDeviceEvidence(
      candidate,
      {
        activeSessionId,
        seenEvidenceIds,
        lastSequenceByDevice,
      },
      (authorizedEvidence) =>
        registryWriter(
          authorizedEvidence,
          payload,
        ),
    );

  if (
    result.registered &&
    result.authorityResult.evidence
  ) {
    seenEvidenceIds.add(evidenceId);

    if (sequence !== undefined) {
      lastSequenceByDevice.set(
        deviceId,
        sequence,
      );
    }
  }

  emitCallsRuntimeEvent({
    source:
      'CallsNativeEvidencePublicationBridge',
    stage: eventType,
    status:
      result.registered
        ? payload.success === false
          ? 'failure'
          : 'success'
        : 'failure',
    callSessionId: sessionId,
    callId:
      nonEmptyString(payload.callId) ??
      sessionId,
    deviceId,
    userId:
      nonEmptyString(payload.userId),
    evidenceId,
    evidenceClass: 'NATIVE_RUNTIME',
    authority:
      result.authorityResult.authority,
    authorityVersion:
      result.authorityResult
        .authorityVersion,
    authorityDecision:
      result.authorityResult.decision,
    rejectionCode:
      result.authorityResult
        .rejectionCode,
    sharedEvidencePublished: false,
    reason:
      result.authorityResult.reason,
    timestamp: capturedAt,
  });
}

function consumeEvidenceUrl(
  rawUrl: string,
): void {
  let url: URL;

  try {
    url = new URL(
      rawUrl,
      window.location.origin,
    );
  } catch {
    return;
  }

  const activeSessionId =
    url.searchParams.get(
      'callSessionId',
    ) ??
    url.searchParams.get(
      'call_session_id',
    );

  if (!activeSessionId) {
    return;
  }

  const rawEvidenceValues =
    url.searchParams.getAll(
      EVIDENCE_QUERY_PARAMETER,
    );

  for (
    const rawEvidence
    of rawEvidenceValues
  ) {
    for (
      const payload
      of parsePayloads(rawEvidence)
    ) {
      publishPayload(
        payload,
        activeSessionId,
      );
    }
  }

  if (
    rawEvidenceValues.length > 0 &&
    url.origin === window.location.origin
  ) {
    url.searchParams.delete(
      EVIDENCE_QUERY_PARAMETER,
    );

    window.history.replaceState(
      window.history.state,
      document.title,
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
}

export function startCallsNativeEvidencePublicationBridge(): void {
  if (
    publicationBridgeStarted ||
    typeof window === 'undefined'
  ) {
    return;
  }

  publicationBridgeStarted = true;

  consumeEvidenceUrl(
    window.location.href,
  );

  void CapacitorApp.addListener(
    'appUrlOpen',
    ({ url }) => {
      consumeEvidenceUrl(url);
    },
  );
}
